import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { 
  SequenceFormData, 
  sequenceSchema, 
  UploadedFile, 
  WizardStep,
  WIZARD_STEPS 
} from '../types';
import { SpotifyTrack } from '@/services/spotifyService';

export const useUploadWizard = (
  onSuccess?: (sequenceId: string) => void,
  onCancel?: () => void
) => {
  const { user, validateSession } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [selectedSpotifyTrack, setSelectedSpotifyTrack] = useState<SpotifyTrack | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<SequenceFormData>({
    resolver: zodResolver(sequenceSchema),
    defaultValues: {
      is_free: false,
      price: 0,
      software_type: 'xLights',
      holiday_type: 'Christmas',
    },
  });

  const getCurrentStepId = (): WizardStep => {
    return WIZARD_STEPS[currentStep].id;
  };

  const goToStep = (step: number) => {
    if (step >= 0 && step < WIZARD_STEPS.length) {
      setCurrentStep(step);
    }
  };

  const goNext = () => {
    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const uploadFileToStorage = useCallback(async (file: File) => {
    if (!user) {
      toast.error('You must be logged in to upload files');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // First, validate that we have a valid session and refresh if needed
      const isSessionValid = await validateSession();
      
      if (!isSessionValid) {
        console.error('Session validation failed');
        toast.error('Your session has expired. Please log in again.');
        setUploadedFile(null);
        return;
      }

      // Get the current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.error('Session retrieval failed:', sessionError);
        toast.error('Authentication error. Please log in again.');
        setUploadedFile(null);
        return;
      }

      // Generate unique filename with safer path structure
      const fileExtension = file.name.split('.').pop();
      const safeFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `${session.user.id}/${Date.now()}-${safeFileName}`;

      // Determine content type explicitly
      const contentType = file.type || (fileName.endsWith('.zip') ? 'application/zip' : 'application/octet-stream');
      
      console.log('Attempting file upload:', {
        fileName,
        fileSize: file.size,
        fileType: file.type,
        explicitContentType: contentType,
        userId: session.user.id,
        sessionValid: !!session,
        userAuthenticated: !!user
      });

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      // Upload file to Supabase storage with explicit content type
      const { data: fileData, error: fileError } = await supabase.storage
        .from('sequences')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: contentType
        });

      clearInterval(progressInterval);

      if (fileError) {
        console.error('Supabase upload error:', fileError);
        
        // Handle specific error types
        if (fileError.message?.includes('401') || fileError.message?.includes('unauthorized')) {
          toast.error('Authentication error. Please log in again.');
        } else if (fileError.message?.includes('403') || fileError.message?.includes('forbidden')) {
          toast.error('Permission denied. Please check your account permissions.');
        } else if (fileError.message?.includes('413') || fileError.message?.includes('too large')) {
          toast.error('File is too large. Maximum size is 100MB.');
        } else {
          toast.error(`Upload failed: ${fileError.message}`);
        }
        
        throw fileError;
      }

      console.log('Upload successful:', fileData);
      setUploadProgress(100);
      
      // Store file info for later use
      setUploadedFile(prev => {
        if (prev) {
          return Object.assign(prev, { 
            uploadedPath: fileName,
            uploadedSize: file.size 
          });
        }
        return null;
      });
      
      toast.success('File uploaded successfully!');
    } catch (error) {
      console.error('File upload error:', error);
      
      // More specific error handling
      if (error instanceof Error) {
        if (error.message?.includes('network') || error.message?.includes('fetch')) {
          toast.error('Network error. Please check your connection and try again.');
        } else if (!error.message?.includes('Upload failed:')) {
          toast.error('Failed to upload file. Please try again.');
        }
      } else {
        toast.error('Failed to upload file. Please try again.');
      }
      
      setUploadedFile(null);
    } finally {
      setIsUploading(false);
    }
  }, [user]);

  const handleFileSelect = useCallback(async (file: File) => {
    // Create an UploadedFile object with the File properties
    const uploadedFileObj: UploadedFile = {
      ...file,
      uploadedPath: undefined,
      uploadedSize: undefined,
      // Preserve the original File properties explicitly
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    } as UploadedFile;
    
    setUploadedFile(uploadedFileObj);
    
    // Auto-populate title from filename (remove extension)
    const titleFromFile = file.name.replace(/\.[^/.]+$/, '');
    form.setValue('title', titleFromFile);
    
    // Start file upload
    toast.success('File selected! Starting upload...');
    try {
      await uploadFileToStorage(file);
      
      // Auto-advance to next step after successful upload
      setTimeout(() => {
        toast.success('Moving to next step...');
        goNext();
      }, 1500); // Small delay to show success message
    } catch (error) {
      // Upload failed, don't auto-advance
      console.error('Upload failed:', error);
    }
  }, [form, uploadFileToStorage, goNext]);

  const handleFileRemove = useCallback(() => {
    setUploadedFile(null);
    setUploadProgress(0);
  }, []);

  const handleSpotifyTrackSelect = useCallback((track: SpotifyTrack | null) => {
    setSelectedSpotifyTrack(track);
  }, []);

  const handleSubmit = async () => {
    if (!user) {
      toast.error('You must be logged in to submit a sequence');
      return;
    }

    if (!uploadedFile?.uploadedPath) {
      toast.error('No file uploaded');
      return;
    }

    const formData = form.getValues();
    
    setIsSubmitting(true);

    try {
      // Create sequence record in database
      const { data: sequence, error: sequenceError } = await supabase
        .from('sequences')
        .insert({
          user_id: user.id,
          title: formData.title,
          description: formData.description,
          software_type: formData.software_type,
          holiday_type: formData.holiday_type,
          price: formData.is_free ? 0 : formData.price,
          is_free: formData.is_free,
          file_url: uploadedFile.uploadedPath,
          spotify_track_id: selectedSpotifyTrack?.id,
        })
        .select()
        .single();

      if (sequenceError) throw sequenceError;

      toast.success('Sequence submitted successfully!');
      
      if (onSuccess && sequence) {
        onSuccess(sequence.id);
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit sequence. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    // State
    currentStep,
    uploadedFile,
    selectedSpotifyTrack,
    uploadProgress,
    isUploading,
    isSubmitting,
    form,
    
    // Navigation
    getCurrentStepId,
    goToStep,
    goNext,
    goBack,
    
    // Actions
    handleFileSelect,
    handleFileRemove,
    handleSpotifyTrackSelect,
    handleSubmit,
    onCancel,
    
    // Computed
    totalSteps: WIZARD_STEPS.length,
    isFirstStep: currentStep === 0,
    isLastStep: currentStep === WIZARD_STEPS.length - 1,
    currentStepInfo: WIZARD_STEPS[currentStep],
  };
};